/**
 * Pure helpers for the Team → Users bulk actions. Kept framework-free so the
 * safety-critical invariants are unit-tested without rendering the page:
 *   1. a bulk action never targets the acting user themselves, and never a user
 *      the actor isn't allowed to manage (the server re-checks, but the UI must
 *      not even offer it); and
 *   2. a partial failure is reported accurately (which ids succeeded / failed),
 *      so the operator is never told "done" when some calls were rejected.
 *
 * These are shared by the single owner of the bulk flow in TeamPageV2 — there is
 * no second copy of this logic.
 */

/**
 * Resolve a selection of user ids to the users a given actor may actually act on.
 * @param {Iterable<string>} ids           selected user ids
 * @param {Map<string, object>} usersById  id → user record
 * @param {string|undefined} currentUserId the acting user's id (always excluded)
 * @param {(u: object) => boolean} canManage authority predicate (mirrors the server rule)
 * @returns {object[]} the eligible target user records, in selection order
 */
export function selectBulkTargets(ids, usersById, currentUserId, canManage) {
  const out = [];
  for (const id of ids || []) {
    const u = usersById && usersById.get ? usersById.get(id) : undefined;
    if (!u) continue;
    if (currentUserId && u.id === currentUserId) continue; // never self
    if (typeof canManage === 'function' && !canManage(u)) continue; // never unmanageable
    out.push(u);
  }
  return out;
}

/**
 * Tally Promise.allSettled results from per-user API calls. A call counts as a
 * success only when it fulfilled AND the resolved body is not an explicit
 * failure envelope ({ ok: false }) — apiFetch resolves rejections as
 * { ok: false, ... } rather than throwing, so a naive fulfilled check would
 * over-count successes.
 * @param {Array<{status: string, value?: any}>} results
 * @returns {{ ok: number, failedIdx: number[] }}
 */
export function summarizeSettled(results) {
  let ok = 0;
  const failedIdx = [];
  (results || []).forEach((r, i) => {
    const good = r && r.status === 'fulfilled' && r.value && r.value.ok !== false;
    if (good) ok += 1; else failedIdx.push(i);
  });
  return { ok, failedIdx };
}
